import { useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/FileDownloadOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  IInsightsReportCatalogItem,
  IInsightsReportParamSpec,
} from "v2/services/insights.service";
import { formatCpfList, parseCpfList } from "../parseCpfList";
import {
  MIN_REPORT_DATE,
  rangeLengthInDays,
  todayIsoLocal,
  MAX_RANGE_DAYS,
} from "./reportDateRange";
import {
  CONDUCCO_ORANGE,
  CONDUCCO_PRIMARY,
  INSIGHTS_BORDER,
  INSIGHTS_FAINT,
  INSIGHTS_FONT,
  INSIGHTS_MUTED,
  INSIGHTS_TEXT,
} from "../tokens";
import { reportIcon } from "./reportIcon";
import {
  actionButtonSx,
  COMPACT_FIELD_SX,
  GROUP_LABEL_SX,
  MODE_TOGGLE_SX,
  COMPACT_TEXTAREA_SX,
} from "./reportStyles";
import {
  buildReportRequest,
  EMPTY_PARAM_VALUES,
  hasRequiredParams,
  IReportParamValues,
} from "./reportParams";

export interface ReportCardProps {
  report: IInsightsReportCatalogItem;
  /** `true` enquanto **este** card gera. */
  generating: boolean;
  /** `true` enquanto qualquer outro gera, ou sem workspace escolhido. */
  disabled?: boolean;
  onGenerate: (
    report: IInsightsReportCatalogItem,
    params: { cpfs?: string[]; desde?: string; ate?: string }
  ) => void;
}

/**
 * Um relatório do catálogo.
 *
 * O card é lido em duas passadas: o cabeçalho diz o que é (ícone, título,
 * cadência), o corpo pede o que falta e o rodapé mostra o que vai ser gerado ao
 * lado do botão. O accent do relatório aparece só no ícone, no filete e no
 * botão — pintar o título dele também era o que deixava tudo da mesma cor, sem
 * dizer o que era título e o que era ação.
 *
 * Card indisponível continua na tela, desabilitado, com o motivo do backend
 * escrito. Esconder o card faria o usuário concluir que o relatório não existe;
 * o motivo ("a tabela TIMELINE_CPF não existe neste workspace") é a informação
 * que ele precisa.
 */
export function ReportCard({
  report,
  generating,
  disabled,
  onGenerate,
}: ReportCardProps) {
  const [values, setValues] = useState<IReportParamValues>(EMPTY_PARAM_VALUES);
  /**
   * Dia único e intervalo são **perguntas diferentes**, não um atalho de uma
   * para a outra. Quem quer um dia preenchia dois campos com a mesma data — e o
   * link que copiava a data não parecia clicável, então quase ninguém usava.
   *
   * Intervalo é o padrão: é o que o relatório responde na maior parte das vezes.
   */
  const [modoData, setModoData] = useState<"intervalo" | "dia">("intervalo");
  // O campo vem junto da mensagem: erro de CPF pertence ao campo de CPF, não a
  // uma linha solta no rodapé que não diz o que corrigir.
  const [error, setError] = useState<{
    message: string;
    field?: IInsightsReportParamSpec["kind"];
  } | null>(null);

  // Calculado no render, não em módulo: um painel aberto atravessa a
  // meia-noite, e um `today` congelado no carregamento passaria a proibir o dia
  // corrente.
  const hoje = todayIsoLocal();

  const accent = report.accent === "orange" ? CONDUCCO_ORANGE : CONDUCCO_PRIMARY;
  const indisponivel = !report.disponivel;
  const bloqueado = indisponivel || disabled || generating;
  const pronto = hasRequiredParams(report.params, values);
  const Icone = reportIcon(report.id);

  const setValue = (key: keyof IReportParamValues, value: string) => {
    setValues((prev) => ({ ...prev, [key]: value }));
    setError(null);
  };

  /**
   * Valida ao sair do campo, não a cada tecla: acusar "CPF inválido" no
   * terceiro dígito é ruído, já que todo CPF passa por inválido enquanto está
   * sendo digitado. Sendo válido, normaliza para máscara — é a confirmação de
   * que a colagem virou N CPFs, e não um texto que ninguém conferiu.
   */
  const handleCpfBlur = (max: number) => {
    const raw = values.cpfs.trim();
    if (!raw) {
      setError(null);
      return;
    }
    const result = parseCpfList(raw, max);
    if (result.error) {
      setError({ message: result.error, field: "lista-cpf" });
      return;
    }
    setValues((prev) => ({ ...prev, cpfs: formatCpfList(result.cpfs) }));
    setError(null);
  };

  const handleGenerate = () => {
    const result = buildReportRequest(report.params, values, hoje);
    if (result.error || !result.params) {
      setError({ message: result.error!, field: result.errorField });
      return;
    }
    setError(null);
    onGenerate(report, result.params);
  };

  const temPeriodo = report.params.some((s) => s.kind === "intervalo-data");
  // O tamanho do período fica visível antes de gerar: "394 dias" contra o teto
  // explica a recusa melhor que a mensagem sozinha, e na maioria das vezes
  // evita que ela apareça. Mora no rodapé, ao lado do botão, e não sob o campo:
  // é a última coisa lida antes de clicar, e economiza uma linha de altura.
  const dias = temPeriodo ? rangeLengthInDays(values.desde, values.ate) : 0;
  const periodoExcede = dias > MAX_RANGE_DAYS;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1.25,
        p: 1.75,
        borderRadius: "14px",
        border: `1px solid ${INSIGHTS_BORDER}`,
        backgroundColor: "#FFFFFF",
        opacity: indisponivel ? 0.72 : 1,
        transition: "box-shadow 0.15s ease, border-color 0.15s ease",
        "&:hover": indisponivel
          ? undefined
          : {
              borderColor: "#CFCFDD",
              boxShadow: "0 6px 18px rgba(26,26,46,0.08)",
            },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.25 }}>
        {/* O ícone é o que diferencia um card do outro de relance — antes os
            três eram blocos de texto idênticos empilhados. */}
        <Box
          sx={{
            flexShrink: 0,
            width: 30,
            height: 30,
            borderRadius: "9px",
            display: "grid",
            placeItems: "center",
            backgroundColor: indisponivel
              ? "rgba(26,26,46,0.05)"
              : `${accent}1A`,
          }}
        >
          <Icone
            sx={{
              // `.MuiSvgIcon-root { color: #212121 }` do tema global atinge
              // este ícone direto; sem `!important` ele sai preto.
              color: `${indisponivel ? INSIGHTS_FAINT : accent} !important`,
              fontSize: 17,
            }}
          />
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "baseline",
              justifyContent: "space-between",
              gap: 1,
            }}
          >
            <Typography
              sx={{
                fontFamily: INSIGHTS_FONT,
                fontSize: "0.875rem",
                fontWeight: 600,
                lineHeight: 1.35,
                color: INSIGHTS_TEXT,
              }}
            >
              {report.titulo}
            </Typography>
            {/* Cadência como rótulo, não como `Chip`: o `Chip` do tema tem
                altura própria e virava um segundo elemento clicável à toa. */}
            <Typography
              component="span"
              sx={{
                flexShrink: 0,
                fontFamily: INSIGHTS_FONT,
                fontSize: "0.625rem",
                fontWeight: 700,
                letterSpacing: 0.5,
                textTransform: "uppercase",
                color: INSIGHTS_FAINT,
              }}
            >
              {report.cadence}
            </Typography>
          </Box>
          <Typography
            title={report.descricao}
            sx={{
              fontFamily: INSIGHTS_FONT,
              fontSize: "0.75rem",
              lineHeight: 1.5,
              color: INSIGHTS_MUTED,
              mt: 0.375,
              // Duas linhas no máximo: descrições de tamanhos diferentes
              // deixavam os cards da mesma linha desalinhados. O texto inteiro
              // continua acessível no `title`.
              display: "-webkit-box",
              WebkitBoxOrient: "vertical",
              WebkitLineClamp: 2,
              overflow: "hidden",
            }}
          >
            {report.descricao}
          </Typography>
        </Box>
      </Box>

      {indisponivel && (
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 0.875,
            px: 1.25,
            py: 1,
            borderRadius: "10px",
            backgroundColor: "rgba(224,113,36,0.08)",
          }}
        >
          <InfoOutlinedIcon
            sx={{ color: `${CONDUCCO_ORANGE} !important`, fontSize: 15, mt: "1px" }}
          />
          <Typography
            sx={{
              fontFamily: INSIGHTS_FONT,
              fontSize: "0.6875rem",
              lineHeight: 1.5,
              color: INSIGHTS_TEXT,
            }}
          >
            {report.motivo ?? "Este relatório não está disponível neste workspace."}
          </Typography>
        </Box>
      )}

      {!indisponivel && report.params.length > 0 && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
          {report.params.map((spec) =>
            spec.kind === "intervalo-data" ? (
              <Box
                key="intervalo-data"
                sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}
              >
                {/* Nome do grupo e modo na mesma linha: o rótulo é dito uma vez,
                    e a escolha fica ao lado do que ela governa. Antes o nome
                    vinha como rótulo flutuante do primeiro campo — longo,
                    encostava no toggle, e repetia a palavra "Intervalo" que o
                    próprio toggle já dizia. */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                    flexWrap: "wrap",
                  }}
                >
                  <Typography sx={GROUP_LABEL_SX}>{spec.label}</Typography>

                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={modoData}
                    disabled={bloqueado}
                    aria-label="Modo de período"
                    onChange={(_, proximo: "intervalo" | "dia" | null) => {
                      if (!proximo) return;
                      setModoData(proximo);
                      // Fecha o intervalo no que já foi digitado, em vez de
                      // limpar: quem preencheu "de" não perde o que digitou.
                      if (proximo === "dia" && values.desde) {
                        setValue("ate", values.desde);
                      }
                    }}
                    sx={MODE_TOGGLE_SX}
                  >
                    <ToggleButton value="intervalo">Intervalo</ToggleButton>
                    <ToggleButton value="dia">Dia único</ToggleButton>
                  </ToggleButtonGroup>
                </Box>

                {modoData === "dia" ? (
                  <TextField
                    size="small"
                    type="date"
                    label="Dia"
                    value={values.desde}
                    // Um campo, um valor: as duas pontas do período recebem a
                    // mesma data. A janela do backend é semiaberta, então isto
                    // cobre o dia inteiro, de 00:00 a 23:59.
                    onChange={(e) => {
                      setValue("desde", e.target.value);
                      setValue("ate", e.target.value);
                    }}
                    disabled={bloqueado}
                    error={error?.field === "intervalo-data"}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ min: MIN_REPORT_DATE, max: hoje }}
                    sx={{ ...COMPACT_FIELD_SX, maxWidth: "50%" }}
                  />
                ) : (
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 1,
                    }}
                  >
                    <TextField
                      size="small"
                      type="date"
                      label="De"
                      value={values.desde}
                      onChange={(e) => setValue("desde", e.target.value)}
                      disabled={bloqueado}
                      error={error?.field === "intervalo-data"}
                      InputLabelProps={{ shrink: true }}
                      // O calendário do navegador já não oferece o futuro:
                      // barrar na origem vale mais que recusar depois.
                      inputProps={{ min: MIN_REPORT_DATE, max: hoje }}
                      sx={COMPACT_FIELD_SX}
                    />
                    <TextField
                      size="small"
                      type="date"
                      label="Até"
                      value={values.ate}
                      onChange={(e) => setValue("ate", e.target.value)}
                      disabled={bloqueado}
                      error={error?.field === "intervalo-data"}
                      InputLabelProps={{ shrink: true }}
                      // `min` acompanha a data inicial: o calendário deixa de
                      // oferecer o intervalo invertido em vez de aceitá-lo e
                      // reclamar depois.
                      inputProps={{
                        min: values.desde || MIN_REPORT_DATE,
                        max: hoje,
                      }}
                      sx={COMPACT_FIELD_SX}
                    />
                  </Box>
                )}
              </Box>
            ) : (
              <CpfListField
                key="lista-cpf"
                spec={spec}
                value={values.cpfs}
                error={error}
                disabled={bloqueado}
                onChange={(next) => setValue("cpfs", next)}
                onBlur={() => handleCpfBlur(spec.max)}
              />
            )
          )}
        </Box>
      )}

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          mt: "auto",
          pt: 1.25,
          borderTop: `1px solid #F1F1F6`,
        }}
      >
        {/* Uma linha só à esquerda do botão: o erro, quando existe, toma o
            lugar do resumo — são a mesma informação em estados diferentes. */}
        <Typography
          sx={{
            fontFamily: INSIGHTS_FONT,
            fontSize: "0.6875rem",
            lineHeight: 1.4,
            minWidth: 0,
            color:
              error && error.field !== "lista-cpf"
                ? CONDUCCO_ORANGE
                : periodoExcede
                  ? CONDUCCO_ORANGE
                  : INSIGHTS_FAINT,
          }}
        >
          {error && error.field !== "lista-cpf"
            ? error.message
            : dias > 0
              ? `${dias} ${dias === 1 ? "dia" : "dias"}${
                  periodoExcede ? `; o máximo é ${MAX_RANGE_DAYS}` : ""
                }`
              : ""}
        </Typography>

        <Tooltip
          title={
            indisponivel
              ? report.motivo ?? ""
              : pronto
                ? ""
                : "Preencha os campos acima"
          }
        >
          <span>
            <Button
              variant="contained"
              disableElevation
              disabled={bloqueado || !pronto}
              onClick={handleGenerate}
              startIcon={
                generating ? (
                  <CircularProgress size={15} />
                ) : (
                  <DownloadIcon />
                )
              }
              sx={actionButtonSx(accent)}
            >
              {generating ? "Gerando" : "Gerar relatório"}
            </Button>
          </span>
        </Tooltip>
      </Box>
    </Box>
  );
}

interface CpfListFieldProps {
  spec: Extract<IInsightsReportParamSpec, { kind: "lista-cpf" }>;
  value: string;
  error: { message: string; field?: IInsightsReportParamSpec["kind"] } | null;
  disabled?: boolean;
  onChange: (value: string) => void;
  onBlur: () => void;
}

/**
 * O campo de CPFs.
 *
 * Aceita máscara ou dígito puro, e qualquer separador — quem investiga dez CPFs
 * cola uma coluna de planilha. O que vai no payload é sempre só dígito.
 *
 * A contagem embaixo do campo é o ponto: numa lista de dez, "8 CPFs" contra os
 * dez que a pessoa acha que colou é o que denuncia repetido ou linha perdida
 * antes de gerar o relatório errado.
 */
function CpfListField({
  spec,
  value,
  error,
  disabled,
  onChange,
  onBlur,
}: CpfListFieldProps) {
  const hasError = error?.field === "lista-cpf";
  // Sem `max` aqui de propósito: o excesso é dito pela contagem ("11 de até
  // 10"), não por um erro enquanto a pessoa ainda está colando.
  const { cpfs } = parseCpfList(value, Number.MAX_SAFE_INTEGER);
  const excedeu = cpfs.length > spec.max;

  const helperText = hasError
    ? error!.message
    : excedeu
      ? `${cpfs.length} CPFs; o máximo é ${spec.max}`
      : cpfs.length > 0
        ? `${cpfs.length} ${cpfs.length === 1 ? "CPF" : "CPFs"} de até ${spec.max}`
        : "Um por linha ou separados por vírgula";

  return (
    <TextField
      size="small"
      fullWidth
      multiline
      minRows={1}
      maxRows={4}
      label={`${spec.label} (até ${spec.max})`}
      placeholder="529.982.247-25, 111.444.777-35"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onBlur={onBlur}
      disabled={disabled}
      error={hasError || excedeu}
      helperText={helperText}
      InputLabelProps={{ shrink: true }}
      inputProps={{ spellCheck: false, autoComplete: "off" }}
      sx={COMPACT_TEXTAREA_SX}
    />
  );
}
